/* Alyssa's Cookies — cart + slide-in drawer (shared across all pages)
   ------------------------------------------------------------------
   Wired to Shopify's real cart via the Cart AJAX API:
     POST /cart/add.js     — add a variant
     POST /cart/change.js  — update / remove a line (quantity: 0 removes it)
     GET  /cart.js         — fetch the current cart as JSON

   There's no localStorage here anymore — the cart lives in Shopify's
   own cart cookie, which is what /checkout reads from too, so the
   Checkout button just links straight to /checkout.

   Markup/classes are unchanged from the original build (cart-drawer,
   cart-item, data-inc/data-dec/data-remove, cart-btn__count, etc.) —
   this file re-renders that same markup after every cart change so
   the CSS still applies without modification.

   Buttons that add to cart only need two data-attributes now:
     data-id   — the VARIANT ID to add (not a custom SKU string)
     data-qty  — optional quantity override (see the PDP qty stepper
                 in main.js); defaults to 1
   data-name / data-price / data-image / data-tint are no longer read
   by this file — Shopify returns the authoritative name/price/image
   for every line, so they're safe to leave on the markup (harmless)
   or drop them. */
(function () {
  "use strict";

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- DOM refs ---------- */
  var drawer = document.getElementById("cartDrawer");
  var itemsEl = document.getElementById("cartItems");
  var footEl = document.getElementById("cartFoot");
  var shipEl = document.getElementById("cartShip");
  var subEl = document.getElementById("cartSubtotal");
  var closeBtn = document.getElementById("cartClose");

  var FREE_SHIP_DOLLARS = parseFloat((drawer && drawer.dataset.freeShipping)) || 45;
  var FREE_SHIP_CENTS = Math.round(FREE_SHIP_DOLLARS * 100);
  var MONEY_FORMAT = (drawer && drawer.dataset.moneyFormat) || "${{amount}}";

  /* Kept in sync with Shopify after every fetch so line-item buttons
     (+/-/remove) can look up a line's current quantity without
     re-parsing the DOM. */
  var currentCart = null;

  /* ---------- Money formatting ----------
     Shopify's cart JSON returns every amount in cents. This mirrors
     the shop's own money_format setting (e.g. "${{amount}}",
     "{{amount_no_decimals}} USD", etc.) so prices match the rest of
     the store exactly. */
  var formatMoney = function (cents, format) {
    format = format || MONEY_FORMAT;
    if (typeof cents === "string") cents = cents.replace(".", "");

    var withDelimiters = function (number, precision, thousands, decimalSep) {
      precision = typeof precision === "undefined" ? 2 : precision;
      thousands = typeof thousands === "undefined" ? "," : thousands;
      decimalSep = typeof decimalSep === "undefined" ? "." : decimalSep;
      if (isNaN(number) || number == null) return "0";
      number = (number / 100).toFixed(precision);
      var parts = number.split(".");
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + thousands);
      var centsPart = parts[1] ? decimalSep + parts[1] : "";
      return dollars + centsPart;
    };

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case "amount_no_decimals":
        value = withDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = withDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = withDelimiters(cents, 0, ".", ",");
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  };

  /* Cart image URLs from the Cart AJAX API are protocol-relative
     ("//cdn.shopify.com/…") — fine in an <img> src, but background-image
     needs a full URL in some browsers. */
  var normalizeImage = function (url) {
    if (!url) return "";
    return url.indexOf("//") === 0 ? "https:" + url : url;
  };

  /* ---------- Count badge (present on every page header) ---------- */
  var syncBadge = function (cart) {
    var q = cart ? cart.item_count : 0;
    document.querySelectorAll(".cart-btn__count").forEach(function (b) {
      b.textContent = String(q);
    });
    document.querySelectorAll(".cart-btn").forEach(function (b) {
      b.setAttribute("aria-label", "Cart, " + q + " item" + (q === 1 ? "" : "s"));
    });
  };

  /* ---------- Transient error banner ---------- */
  var showError = function (message) {
    if (!itemsEl) return;
    var existing = itemsEl.querySelector(".cart-drawer__error");
    if (existing) existing.remove();
    var banner = document.createElement("div");
    banner.className = "cart-drawer__error";
    banner.setAttribute("role", "alert");
    banner.textContent = message;
    itemsEl.prepend(banner);
    setTimeout(function () {
      banner.remove();
    }, 5000);
  };

  /* ---------- Render drawer contents from a Shopify cart object ---------- */
  var render = function (cart) {
    syncBadge(cart);
    if (!drawer || !itemsEl) return;

    var noteFieldEl = document.getElementById("cartNoteField");

    if (!cart.item_count) {
      itemsEl.innerHTML =
        '<div class="cart-drawer__empty">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" style="color:var(--pink)"><use href="#ic-cart"/></svg>' +
          "<p>Your cart is feeling a little empty.<br>Let's fix that.</p>" +
          '<a href="/collections/all" class="btn btn--primary">Shop Cookies</a>' +
        "</div>";
      if (footEl) footEl.hidden = true;
      if (shipEl) shipEl.hidden = true;
      if (noteFieldEl) noteFieldEl.hidden = true;
      return;
    }

    if (footEl) footEl.hidden = false;
    if (shipEl) shipEl.hidden = false;
    if (noteFieldEl) noteFieldEl.hidden = false;

    /* Free-shipping meter */
    if (shipEl) {
      var pct = Math.min(100, (cart.total_price / FREE_SHIP_CENTS) * 100);
      var remainingCents = FREE_SHIP_CENTS - cart.total_price;
      shipEl.innerHTML =
        (remainingCents > 0
          ? "<span>You're <strong>" + formatMoney(remainingCents) + "</strong> away from free shipping.</span>"
          : "<span>🎉 You've unlocked <strong>free shipping!</strong></span>") +
        '<div class="cart-drawer__bar"><span style="width:' + pct.toFixed(1) + '%"></span></div>';
    }

    /* Line items */
    /* Line items */
    itemsEl.innerHTML = cart.items.map(function (item) {
      var title = item.product_title || item.title;
      var variantSuffix =
        item.variant_title && item.variant_title !== "Default Title"
          ? " — " + item.variant_title
          : "";

      /* Subscription items carry a selling_plan_allocation object with
         the plan name and both the discounted and pre-discount price */
      var subAllocation = item.selling_plan_allocation;
      var subBadge = "";
      var eachPriceHtml = formatMoney(item.price) + " each";

      if (subAllocation) {
        var planName = (subAllocation.selling_plan && subAllocation.selling_plan.name) || "Subscription";
        subBadge =
          '<span class="cart-item__sub-badge">' +
            '<svg class="ic" aria-hidden="true"><use href="#ic-check"/></svg>' +
            planName +
          "</span>";

        eachPriceHtml = (subAllocation.compare_at_price && subAllocation.compare_at_price > subAllocation.price)
          ? formatMoney(subAllocation.price) + " <s>" + formatMoney(subAllocation.compare_at_price) + "</s> each"
          : formatMoney(subAllocation.price) + " each";
      }

      return (
        '<div class="cart-item" data-id="' + item.key + '" data-variant-id="' + item.id + '">' +
          '<span class="cart-item__thumb" style="background-image:url(\'' + normalizeImage(item.image) + '\');--tint:#F0E4CF"></span>' +
          "<div>" +
            '<div class="cart-item__top">' +
              "<h4>" + title + variantSuffix + "</h4>" +
              '<button type="button" class="cart-item__remove" data-remove aria-label="Remove ' + title + '">✕</button>' +
            "</div>" +
            subBadge +
            '<p class="cart-item__each">' + eachPriceHtml + "</p>" +
            '<div class="cart-item__row">' +
              '<span class="qty-stepper">' +
                '<button type="button" data-dec aria-label="Decrease quantity">−</button>' +
                "<span>" + item.quantity + "</span>" +
                '<button type="button" data-inc aria-label="Increase quantity">+</button>' +
              "</span>" +
              '<span class="cart-item__line">' + formatMoney(item.final_line_price) + "</span>" +
            "</div>" +
          "</div>" +
        "</div>"
      );
    }).join("");

    if (subEl) subEl.textContent = formatMoney(cart.total_price);
  };

  var setCart = function (cart) {
    currentCart = cart;
    render(cart);
  };

  /* ---------- Talk to Shopify ---------- */
  var jsonHeaders = { "Content-Type": "application/json", Accept: "application/json" };

  var fetchCart = function () {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.json();
    });
  };

  var refreshCart = function () {
    return fetchCart().then(setCart);
  };

  /* ---------- Rebuy cart-upsell integration ----------
     Rebuy's widgets add/remove items through their own mechanism,
     bypassing postCartAdd/postCartChange below — so this drawer has
     no idea the cart changed unless we listen for Rebuy's own events
     and refresh ourselves. Listening for both the current and legacy
     event names for safety, since which one fires depends on the
     Rebuy connector version running on the store. */
  document.addEventListener("rebuy:cart.change", function () { refreshCart(); });
  document.addEventListener("rebuy.add", function () { refreshCart(); });
  document.addEventListener("rebuy.change", function () { refreshCart(); });
  document.addEventListener("rebuy.remove", function () { refreshCart(); });

  var postCartAdd = function (variantId, quantity, sellingPlanId) {
    var item = { id: variantId, quantity: quantity };
    if (sellingPlanId) item.selling_plan = sellingPlanId;
    return fetch("/cart/add.js", {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ items: [item] })
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var message = (data && (data.description || data.message)) || "That item couldn't be added.";
          throw new Error(message);
        }
        return data;
      });
    });
  };

  var postCartChange = function (lineKey, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var message = (data && (data.description || data.message)) || "Couldn't update your cart.";
          throw new Error(message);
        }
        return data; // /cart/change.js returns the full, updated cart
      });
    });
  };

  /* Shopify's Cart API has no endpoint to add/edit properties on a line
     that's already in the cart — /cart/change.js only touches quantity.
     So "saving a note as a property" means: snapshot every current line,
     clear the cart, then re-add every line with the Note property baked
     into the add.js call this time. This is why it's a bigger operation
     than a plain /cart/update.js call. */
  var postCartNoteAsProperties = function (note) { return fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ note: (note || "").trim() }) }).then(function (response) { return response.json().then(function (data) { if (!response.ok) { throw new Error((data && (data.description || data.message)) || "Couldn't save your note."); } return data; }); });
    note = (note || "").trim();
    if (!note) {
      return Promise.resolve(currentCart); // nothing to do — don't touch the cart
    }
    if (!currentCart || !currentCart.items.length) {
      return Promise.resolve(currentCart); // empty cart, nothing to attach a note to
    }

    var items = currentCart.items.map(function (item) {
      var properties = {};
      if (item.properties) {
        Object.keys(item.properties).forEach(function (key) {
          if (item.properties[key] != null) properties[key] = item.properties[key];
        });
      }
      properties["Note"] = note;

      var payload = {
        id: item.variant_id,
        quantity: item.quantity,
        properties: properties
      };
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        payload.selling_plan = item.selling_plan_allocation.selling_plan.id;
      }
      return payload;
    });

    return fetch("/cart/clear.js", { method: "POST", headers: jsonHeaders })
      .then(function (response) {
        if (!response.ok) throw new Error("Couldn't save your note.");
        return fetch("/cart/add.js", {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var message = (data && (data.description || data.message)) || "Couldn't save your note.";
            throw new Error(message);
          }
          return fetchCart(); // re-add.js only returns the new lines, not the full cart
        });
      });
  };

  /* ---------- Open / close ---------- */
  var openDrawer = function () {
    if (!drawer) return;
    drawer.classList.add("open");
    drawer.setAttribute("aria-hidden", "false");
    if (closeBtn) closeBtn.focus();
  };
  var closeDrawer = function () {
    if (!drawer) return;
    drawer.classList.remove("open");
    drawer.setAttribute("aria-hidden", "true");
  };

  /* ---------- Add to cart ---------- */
  var handleAddClick = function (btn) {
    if (btn.disabled || btn.hasAttribute("disabled")) return;

    var variantId = btn.getAttribute("data-id");
    var qty = parseInt(btn.getAttribute("data-qty"), 10);
    if (!qty || qty < 1) qty = 1;
    var sellingPlanId = btn.getAttribute("data-selling-plan") || null;

    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Adding…";

    postCartAdd(variantId, qty, sellingPlanId)
      .then(function () {
        return refreshCart();
      })
      .then(function () {
        openDrawer();
        if (!reducedMotion) {
          btn.animate(
            [{ transform: "scale(1)" }, { transform: "scale(.92)" }, { transform: "scale(1)" }],
            { duration: 260, easing: "cubic-bezier(.34,1.8,.5,1)" }
          );
        }
      })
      .catch(function (err) {
        showError(err.message);
        openDrawer();
      })
      .finally(function () {
        btn.disabled = false;
        btn.textContent = originalLabel;
      });
  };

  document.addEventListener("click", function (e) {
    var btn = e.target.closest ? e.target.closest("[data-add]") : null;
    if (!btn) return;
    e.preventDefault();
    handleAddClick(btn);
  });

  /* ---------- Buy it now: add + go straight to checkout ---------- */
    var handleBuyNowClick = function (btn) {
    if (btn.disabled || btn.hasAttribute("disabled")) return;

    var variantId = btn.getAttribute("data-id");
    var qty = parseInt(btn.getAttribute("data-qty"), 10);
    if (!qty || qty < 1) qty = 1;

    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Redirecting…";

    postCartAdd(variantId, qty)
        .then(function () {
        window.location.href = "/checkout";
        })
        .catch(function (err) {
        showError(err.message);
        openDrawer();
        btn.disabled = false;
        btn.textContent = originalLabel;
        });
    };

    document.addEventListener("click", function (e) {
    var btn = e.target.closest ? e.target.closest("[data-buy-now]") : null;
    if (!btn) return;
    e.preventDefault();
    handleBuyNowClick(btn);
    });

  /* ---------- Cart button opens drawer ---------- */
  document.querySelectorAll(".cart-btn").forEach(function (b) {
    b.addEventListener("click", function (e) {
      e.stopPropagation();
      drawer && drawer.classList.contains("open") ? closeDrawer() : openDrawer();
    });
  });

  /* ---------- Line-item controls (event delegation) ---------- */
  if (itemsEl) {
    itemsEl.addEventListener("click", function (e) {
      var row = e.target.closest ? e.target.closest(".cart-item") : null;
      if (!row || !currentCart) return;

      var key = row.getAttribute("data-id");
      var item = null;
      for (var i = 0; i < currentCart.items.length; i++) {
        if (currentCart.items[i].key === key) { item = currentCart.items[i]; break; }
      }
      if (!item) return;

      var newQty;
      if (e.target.closest("[data-inc]")) newQty = item.quantity + 1;
      else if (e.target.closest("[data-dec]")) newQty = item.quantity - 1; // 0 removes the line
      else if (e.target.closest("[data-remove]")) newQty = 0;
      else return;

      row.classList.add("is-updating");
      postCartChange(key, newQty)
        .then(setCart)
        .catch(function (err) {
          showError(err.message);
          row.classList.remove("is-updating");
        });
    });
  }

  /* ---------- Cart note ---------- */
  var noteToggle = document.getElementById("cartNoteToggle");
  var noteBody = document.getElementById("cartNoteBody");
  var noteInput = document.getElementById("cartNoteInput");
  var noteSaveBtn = document.getElementById("cartNoteSave");
  var noteToggleLabel = document.getElementById("cartNoteToggleLabel");

  if (noteToggle && noteBody) {
    noteToggle.addEventListener("click", function () {
      var expanded = noteToggle.getAttribute("aria-expanded") === "true";
      noteToggle.setAttribute("aria-expanded", String(!expanded));
      noteBody.hidden = expanded;
      if (!expanded) noteInput.focus();
    });
  }

  if (noteSaveBtn && noteInput) {
    noteSaveBtn.addEventListener("click", function () {
      var noteText = noteInput.value.trim();
      if (!noteText) {
        noteInput.focus();
        return; // nothing typed — don't touch the cart at all
      }

      var originalLabel = noteSaveBtn.textContent;
      noteSaveBtn.disabled = true;
      noteSaveBtn.textContent = "Saving…";

      postCartNoteAsProperties(noteText)
        .then(function (cart) {
          setCart(cart);
          noteSaveBtn.textContent = "Saved!";
          if (noteToggleLabel) noteToggleLabel.textContent = "Order note added";
          setTimeout(function () {
            noteSaveBtn.textContent = originalLabel;
            noteSaveBtn.disabled = false;
          }, 1500);
        })
        .catch(function (err) {
          showError(err.message);
          noteSaveBtn.textContent = originalLabel;
          noteSaveBtn.disabled = false;
        });
    });
  }

  /* ---------- Close interactions ---------- */
  if (closeBtn) closeBtn.addEventListener("click", closeDrawer);

  document.addEventListener("click", function (e) {
    if (!drawer || !drawer.classList.contains("open")) return;
    if (drawer.contains(e.target)) return;
    if (e.target.closest && e.target.closest(".cart-btn")) return; /* handled above */
    closeDrawer();
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && drawer && drawer.classList.contains("open")) closeDrawer();
  });

  drawer && drawer.addEventListener("click", function (e) { e.stopPropagation(); });

  /* ---------- Init ----------
     The drawer's initial HTML already came from Liquid (correct on
     first paint), but we still do one silent background fetch so
     `currentCart` is populated — otherwise +/- clicks on an existing
     cart (from a previous visit) would have nothing to look up until
     the next add-to-cart. */
  refreshCart().catch(function () {
    /* Non-fatal — the Liquid-rendered drawer content stays visible,
       we just won't have currentCart populated until the next
       successful fetch (e.g. the next add-to-cart). */
  });
})();